/**
 * סריקת חשבונית ספק — what the AI read off an uploaded invoice, shaped for the invoice form.
 *
 * Dependency-free: the backend imports it through `@malash-office/types/supplier-invoice-scan`.
 * Values are strings (ids included) because the form holds strings; the form decides what to keep.
 */

/** How sure the reading is. The form paints high green, medium orange, low red. */
export const SCAN_CONFIDENCE_LEVELS = ['high', 'medium', 'low'] as const;
export type ScanConfidence = (typeof SCAN_CONFIDENCE_LEVELS)[number];

export const SCAN_CONFIDENCE_LABELS: Record<ScanConfidence, string> = {
  high: 'ביטחון גבוה',
  medium: 'ביטחון בינוני',
  low: 'ביטחון נמוך'
};

/** The invoice form fields a scan may fill. The allocation fields are the first row's. */
export const SCAN_FIELD_KEYS = [
  'docType',
  'number',
  'invoiceDate',
  'dueDate',
  'currency',
  'net',
  'vatRate',
  'description',
  'orderId',
  'componentLabel',
  'tagIds',
  'categoryId',
  'subcategoryId',
  'projectId'
] as const;
export type ScanFieldKey = (typeof SCAN_FIELD_KEYS)[number];

export interface ScannedValue<T = string> {
  value: T;
  confidence: ScanConfidence;
}

export type ScannedInvoiceFields = Partial<{
  [K in ScanFieldKey]: ScannedValue<K extends 'tagIds' ? string[] : string>;
}>;

/** The supplier as printed on the document — what a new supplier's form starts from. */
export interface ScannedSupplier {
  name: string | null;
  legalName: string | null;
  taxId: string | null;
  address: string | null;
  country: string | null;
  phone: string | null;
  email: string | null;
  website: string | null;
  contactName: string | null;
  /** `foreign` / `domestic`. */
  supplierType: string | null;
  /** A PhCurrency code. */
  currency: string | null;
  /** One of `SUPPLIER_PAYMENT_TERMS`. */
  paymentTerms: string | null;
}

export interface SupplierInvoiceScanDto {
  supplier: {
    /** An existing supplier the document belongs to, or null — then `extracted` is a new one. */
    matchedId: number | null;
    matchedBy: 'taxId' | 'name' | null;
    confidence: ScanConfidence;
    extracted: ScannedSupplier;
  };
  fields: ScannedInvoiceFields;
  /** Hebrew notes worth showing: an unreadable page, a total that does not add up. */
  warnings: string[];
}

/**
 * What the scan accepts: what the model reads *and* the invoice's file storage keeps (the
 * `document` upload kind), since the scanned file becomes the invoice's original. A phone camera
 * hands over JPEG.
 */
export const INVOICE_SCAN_MIME_TYPES = ['application/pdf', 'image/jpeg', 'image/png'] as const;
/** The model's own ceiling for one attached image or PDF. */
export const INVOICE_SCAN_MAX_BYTES = 20 * 1024 * 1024;

export const INVOICE_SCAN_ERROR_CODES = {
  NOT_CONFIGURED: 'INV_SCAN_NOT_CONFIGURED',
  FILE_TYPE: 'INV_SCAN_FILE_TYPE',
  FILE_TOO_LARGE: 'INV_SCAN_FILE_TOO_LARGE',
  FAILED: 'INV_SCAN_FAILED'
} as const;
